'use client';

import {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  Dispatch,
} from 'react';

interface InvoiceState {
  isCreateInvoicePopupOpen: boolean;
  isEditInvoicePopupOpen: boolean;
  isImportInvoicePopupOpen: boolean;
  invoiceId: string | null;
}

type InvoiceAction =
  | { type: 'OPEN_CREATE_INVOICE_POPUP' }
  | { type: 'CLOSE_CREATE_INVOICE_POPUP' }
  | { type: 'OPEN_EDIT_INVOICE_POPUP'; payload: { invoiceId: string } }
  | { type: 'CLOSE_EDIT_INVOICE_POPUP' }
  | { type: 'OPEN_IMPORT_INVOICE_POPUP' }
  | { type: 'CLOSE_IMPORT_INVOICE_POPUP' };

const initialState: InvoiceState = {
  isCreateInvoicePopupOpen: false,
  isEditInvoicePopupOpen: false,
  isImportInvoicePopupOpen: false,
  invoiceId: null,
};

const InvoiceContext = createContext<{
  state: InvoiceState;
  dispatch: Dispatch<InvoiceAction>;
} | null>(null);

function invoiceReducer(
  state: InvoiceState,
  action: InvoiceAction
): InvoiceState {
  switch (action.type) {
    case 'OPEN_CREATE_INVOICE_POPUP':
      if (state.isCreateInvoicePopupOpen) return state;
      return { ...state, isCreateInvoicePopupOpen: true };
    case 'CLOSE_CREATE_INVOICE_POPUP':
      return { ...state, isCreateInvoicePopupOpen: false };
    case 'OPEN_EDIT_INVOICE_POPUP':
      return {
        ...state,
        isEditInvoicePopupOpen: true,
        invoiceId: action.payload.invoiceId,
      };
    case 'CLOSE_EDIT_INVOICE_POPUP':
      return { ...state, isEditInvoicePopupOpen: false, invoiceId: null };
    case 'OPEN_IMPORT_INVOICE_POPUP':
      if (state.isImportInvoicePopupOpen) return state;
      return { ...state, isImportInvoicePopupOpen: true };
    case 'CLOSE_IMPORT_INVOICE_POPUP':
      return { ...state, isImportInvoicePopupOpen: false };
    default:
      return state;
  }
}

export const InvoiceProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(invoiceReducer, initialState);
  return (
    <InvoiceContext.Provider value={{ state, dispatch }}>
      {children}
    </InvoiceContext.Provider>
  );
};

export const useInvoiceContext = () => {
  const context = useContext(InvoiceContext);
  if (!context)
    throw new Error('useInvoiceContext must be used within InvoiceProvider');
  return context;
};
